import TextToSpeechService from "@/services/TextToSpeechService";
import { getSpeechSynthesisLanguage } from "@/constants/languageMap";

let currentAudio = null;

const browserSpeechAvailable = () =>
  typeof window !== "undefined" && "speechSynthesis" in window;

// Fallback when the backend can't generate audio: the browser's built-in voice.
// Returns an object with the same pause()/currentTime surface as an <audio>.
function speakWithBrowser(text, language, { speed, onEnded, onError }) {
  if (!browserSpeechAvailable()) {
    return null;
  }

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = getSpeechSynthesisLanguage(language);
  utterance.rate = typeof speed === "number" ? speed : 1;

  const handle = {
    currentTime: 0,
    pause: () => window.speechSynthesis.cancel(),
    // Holding the utterance keeps Chrome from garbage-collecting it before
    // its "end" event fires.
    utterance,
  };
  utterance.onend = () => {
    if (currentAudio === handle) {
      currentAudio = null;
    }
    onEnded?.();
  };
  utterance.onerror = (event) => {
    if (currentAudio === handle) {
      currentAudio = null;
    }
    // "interrupted"/"canceled" fire when we stop playback ourselves.
    if (event.error !== "interrupted" && event.error !== "canceled") {
      onError?.(event);
    }
  };

  currentAudio = handle;
  window.speechSynthesis.speak(utterance);
  return handle;
}

export async function speakText(text, language, options = {}) {
  const { speed, voice, onEnded, onError } = options;

  stopSpeaking();

  let audioUrl = null;
  try {
    audioUrl = await TextToSpeechService.fetchAudioUrl(text, language, {
      speed,
      voice,
    });
  } catch (error) {
    console.warn("Backend text-to-speech failed; using the browser voice.", error);
  }

  if (!audioUrl) {
    return speakWithBrowser(text, language, { speed, onEnded, onError });
  }

  try {
    currentAudio = new Audio(audioUrl);
    const audio = currentAudio;
    // The backend returns normal-speed audio; slow playback happens here.
    audio.playbackRate = typeof speed === "number" ? speed : 1;
    audio.addEventListener(
      "ended",
      () => {
        if (currentAudio === audio) {
          currentAudio = null;
        }
        onEnded?.();
      },
      { once: true }
    );
    audio.addEventListener(
      "error",
      (event) => {
        if (currentAudio === audio) {
          currentAudio = null;
        }
        onError?.(event);
      },
      { once: true }
    );

    await audio.play();
    return audio;
  } catch (error) {
    currentAudio = null;
    console.error("Failed to play generated speech audio:", error);
    return speakWithBrowser(text, language, { speed, onEnded, onError });
  }
}

export function stopSpeaking() {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.currentTime = 0;
    currentAudio = null;
  }
  // Only cancel when something is queued: Chrome can drop a speak() issued
  // right after an unnecessary cancel().
  if (
    browserSpeechAvailable() &&
    (window.speechSynthesis.speaking || window.speechSynthesis.pending)
  ) {
    window.speechSynthesis.cancel();
  }
}
